<script lang="ts" setup>
	import type { RuleObject } from 'ant-design-vue/lib/form/interface'
	import useAccountApi from '~/services/apis/account'
	import usePurchases from '~/services/apis/purchases-info'
	import type { ListContext } from '~/components/Shared/list-item.vue'
	import type { ComputeAddDependantDtoResponse } from '~/types/Purchase'

	const props = defineProps<{
		show: boolean
		policy?: unknown
	}>()

	const { StringToNumber, currencyToNaira } = useCurrency()
	const { confirmPasswordApi } = useAccountApi()
	const { computeNewDependantSlot } = usePurchases()
	const computing = shallowRef(false)
	const isTermsDialog = shallowRef(false)
	const confirmDialog = shallowRef(false)
	const confirmingPassword = shallowRef(false)
	const computeResult = ref<ComputeAddDependantDtoResponse | null>(null)
	const policyInfo = computed(() => props.policy as PolicyResponse | null)
	const {
		renewalDate,
		totalNoOfDependant: currentNoOfDependant,
		isFamilyPlan,
		coverPeriod
	} = usePolicyInfo(() => ({ policyInfo: props.policy }))
	const formData = reactive({
		number_of_dependant: 0,
		purchase_id: policyInfo.value?.purchase_id as string,
		cover_period: policyInfo.value?.meta?.cover_period || '',
		password: '',
		terms: false
	})
	const emits = defineEmits<{
		(event: 'update:show', param: boolean): void
		(
			event: 'proceed',
			param: {
				summary: object
				record: ComputeAddDependantDto
			}
		): void
	}>()
	const confirmationSummary = computed<ListContext>(() => ({
		'New dependant': computeResult.value?.no_of_dependants_to_be_added || 0,
		'Total dependant': computeResult.value?.new_total_no_of_dependant || 0,
		'New plan premium': {
			value: currencyToNaira(computeResult.value?.new_premium || 0),
			pClass: 'text-primary-2 '
		},
		'Renewal date': renewalDate.value.formatted
	}))

	const staffName = computed(() => {
		return `${policyInfo.value?.meta?.first_name || policyInfo.value?.first_name || '---'} ${
			policyInfo.value?.meta?.last_name || policyInfo.value?.last_name || ''
		}`
	})

	const needToKnow = computed(() => [
		`You are adding ${formData.number_of_dependant || 0} dependant slots to this staff <span class="font-bold">${
			staffName.value
		}</span>`,
		`Please note that an immediate charge will be incurred, and this will result in an increase to your current payment upon the next renewal date - <span class="text-primary-500">${renewalDate.value.formatted}</span>`
	])
	const numberValidator = (_: RuleObject, value: string) => {
		const intValue = StringToNumber(value)

		if (isFamilyPlan.value) {
			let remainingDependant = FAMILYPLANMAX
			let msg = ''
			if (currentNoOfDependant.value > 0) {
				remainingDependant = FAMILYPLANMAX - currentNoOfDependant.value
			}
			if (remainingDependant === 0) {
				msg = 'You have reached the limit of family plan dependant'
			} else if (intValue > remainingDependant) {
				msg = `Staff total dependant slot can not be greater than ${remainingDependant}`
			} else if (intValue < FAMILYPLANMIN) {
				msg = `Staff minimum dependant slot should not be less than ${FAMILYPLANMIN}`
			}
			if (msg) {
				return Promise.reject(new Error(msg))
			}
			return Promise.resolve()
		}
		if (intValue > 0 && !isFamilyPlan.value) {
			return Promise.resolve()
		}
		return Promise.reject(new Error('Value must be greater than 1'))
	}
	const computeSlotValue = async () => {
		resolve(async () => {
			const result = await computeNewDependantSlot({
				purchase_id: policyInfo.value?.purchase_id || '',
				cover_period: policyInfo.value?.meta?.cover_period || '',
				number_of_dependant: StringToNumber(formData.number_of_dependant)
			})
			if (result?.data) {
				computeResult.value = result.data
				confirmDialog.value = true
				emits('update:show', false)
			}
		}, computing)
	}
	const confirmAuthorization = async () => {
		if (currentNoOfDependant.value === FAMILYPLANMAX && isFamilyPlan.value) {
			return useNotification().alert({
				status: 400,
				message: 'You have maxed out the total number of depedant value for family plan'
			})
		}
		resolve(async () => {
			const result = await confirmPasswordApi({
				password: formData.password
			})
			if (result?.data) {
				emits('proceed', {
					summary: {
						Plan: computeResult.value?.plan_name || '',
						'Staff Covered': computeResult.value?.staff_covered,
						'Dependant(s)': computeResult.value?.new_total_no_of_dependant,
						'Cover Period': computeResult.value?.cover_period
					},
					record: {
						purchase_id: policyInfo.value?.purchase_id || '',
						cover_period: coverPeriod.value,
						price: Number(computeResult.value?.new_premium || 0),
						number_of_dependant: StringToNumber(formData.number_of_dependant)
					}
				})
				confirmDialog.value = false
			}
		}, confirmingPassword)
	}
</script>

<template>
	<div>
		<dialogs
			:show="show"
			title="Add New Dependant Slot"
			paragraph="To continue with this step, state how many dependants you would like to add to this staff."
			:ui="{ modal: 'max-w-[526px] py-10 h-auto' }"
			@change="$emit('update:show', false)"
		>
			<div class="mt-12">
				<a-form :model="formData" :validate-messages="antValidateMessages" @finish="computeSlotValue">
					<a-form-item
						label="Number of staff dependants"
						:rules="{ required: true, validator: numberValidator }"
						name="number_of_dependant"
					>
						<a-input
							v-model:value="formData.number_of_dependant"
							v-format="true"
							class="custom-input w-full !bg-gray-50 !px-5"
							autocomplete="off"
							data-testid="number_of_dependant"
						/>
					</a-form-item>

					<a-button
						html-type="submit"
						:loading="computing"
						data-testid="continue to confirm"
						class="default-btn mt-8 w-full justify-center !bg-primary-2 !text-white"
					>
						Continue
					</a-button>
				</a-form>
			</div>
		</dialogs>
		<dialogs-error
			v-model:show="confirmDialog"
			type="warning"
			header="Add new dependant slot"
			wrapper-class="bg-gray-50 px-4 py-8 rounded-md w-full space-y-5"
		>
			<template #paragraph>
				<div class="text-left">
					<span class="text-sm text-blue-950">Need to know</span>
					<content-list-item-disc :list="needToKnow" />
				</div>
			</template>

			<template #p>
				<shared-list-item
					class="mt-8 rounded border border-purple-200 bg-purple-50"
					:lists="confirmationSummary"
				/>
				<a-form :model="formData" :validate-messages="antValidateMessages" @finish="confirmAuthorization">
					<a-form-item v-show="formData.terms" label="Password" required name="password" class="!mt-12">
						<a-input-password
							v-model:value="formData.password"
							autocomplete="new password"
							data-testid="confirm password"
							placeholder="Enter your password"
							class="custom-input w-full !bg-gray-50 !px-5"
						/>
					</a-form-item>

					<a-form-item v-show="!formData.terms" name="terms" required>
						<div class="mt-10 flex w-full items-center justify-center gap-x-2">
							<a-checkbox v-model:checked="formData.terms" data-testid="terms" />
							<span class="text-gray-600">
								I have read and I accept the
								<span
									role="button"
									aria-label="button"
									class="font-medium text-primary-2"
									@click="() => (isTermsDialog = true)"
								>
									Terms & Conditions
								</span>
							</span>
						</div>
					</a-form-item>

					<div class="flex w-full flex-wrap items-center justify-between gap-4 sm:mt-12 sm:gap-10">
						<button
							type="button"
							name="back"
							class="default-btn flex-1 justify-center bg-gray-200"
							@click="() => (formData.terms = false)"
						>
							Back
						</button>

						<a-button
							:disabled="!formData.password || !formData.terms || confirmingPassword"
							:loading="confirmingPassword"
							data-testid="proceed"
							html-type="submit"
							class="default-btn 00 flex-1 justify-center border-primary-400 !bg-primary-2 !text-white disabled:!bg-primary-2"
						>
							Proceed
						</a-button>
					</div>
				</a-form>
				<dialogs-terms-and-condition v-model:show="isTermsDialog" />
			</template>

			<template #button>
				<span />
			</template>
		</dialogs-error>
	</div>
</template>
